/**
 * Browser-side `get_structure_properties`: read-only, auto-run like a read.
 * Reports what the structure details panel and its (i) popover show for one
 * structure — type, approval, generation algorithm, description, colour,
 * visibility, contour count, volume, equivalent-sphere diameter, and the
 * contour statistics (surface area, sphericity, centre of mass, bounding-
 * box extent) — plus a marker's position. Units are converted here, at the
 * boundary, exactly as the panel does: mm → cm, mm² → cm².
 * Ports injected (React-free).
 */
import { resolveRoi } from '../core/roiResolver';
import { asRecord, toListed, type StructureFacts } from '../core';
import type { GetStructurePropertiesOutput } from '../../contract';

/** The raw values the panel reads (mm units, engine statistics). */
export interface RoiDetails {
    readonly roiNumber: number;
    readonly name: string;
    readonly type: string;
    readonly approvalStatus: string;
    readonly generationAlgorithm: string;
    readonly description: string;
    /** 0–255 per channel. */
    readonly color: {
        readonly R: number;
        readonly G: number;
        readonly B: number;
    };
    readonly visible: boolean;
    readonly isPointRoi: boolean;
    readonly contourCount: number;
    readonly pointMm?: readonly number[];
    readonly electronDensityOverride?: number;
    readonly massDensityOverride?: number;
}

/** The engine's SDF statistics (`env.getRoiStatistics`), mm units. */
export interface RoiStats {
    readonly surfaceAreaMm2: number;
    readonly sphericity: number | null;
    readonly centerOfMassMm: readonly number[] | null;
    readonly boundingBox: {
        readonly min: readonly number[];
        readonly max: readonly number[];
    } | null;
}

export interface StructurePropertiesDeps {
    readonly structures: ReadonlyArray<StructureFacts>;
    details(roiNumber: number): RoiDetails | undefined;
    stats(roiNumber: number): RoiStats | undefined;
    volumeCm3(roiNumber: number): number;
}

const hex = (c: RoiDetails['color']) =>
    '#' +
    [c.R, c.G, c.B]
        .map((v) =>
            Math.max(0, Math.min(255, Math.round(v)))
                .toString(16)
                .padStart(2, '0')
        )
        .join('')
        .toUpperCase();

const ref = (c: { roiNumber: number; name: string }) => ({
    roiNumber: c.roiNumber,
    name: c.name,
});
const round = (v: number, dp: number) => Number(v.toFixed(dp));
const cm = (p: readonly number[]) => ({
    x: round(p[0] / 10, 2),
    y: round(p[1] / 10, 2),
    z: round(p[2] / 10, 2),
});

export function getStructureProperties(
    input: unknown,
    deps: StructurePropertiesDeps
): GetStructurePropertiesOutput {
    const roi = asRecord(input)?.roi;
    if (typeof roi !== 'string' || !roi) {
        return {
            status: 'unavailable',
            reason: 'a structure name is required',
        };
    }
    const r = resolveRoi(roi, deps.structures.map(toListed));
    if (r.kind === 'ambiguous') {
        return {
            status: 'ambiguous',
            reason: r.reason,
            candidates: r.candidates.map(ref),
        };
    }
    if (r.kind === 'not-found') {
        return { status: 'not-found', suggestions: r.suggestions.map(ref) };
    }
    const d = deps.details(r.structure.roiNumber);
    if (!d) {
        return {
            status: 'unavailable',
            reason: `'${roi}' is no longer loaded`,
        };
    }
    const out: Extract<GetStructurePropertiesOutput, { status: 'ok' }> = {
        status: 'ok',
        roiNumber: d.roiNumber,
        name: d.name,
        type: d.type,
        approvalStatus: d.approvalStatus,
        generationAlgorithm: d.generationAlgorithm,
        description: d.description,
        color: hex(d.color),
        visible: d.visible,
        isMarker: d.isPointRoi,
        contourCount: d.contourCount,
        ...(d.electronDensityOverride !== undefined
            ? { electronDensityOverride: d.electronDensityOverride }
            : {}),
        ...(d.massDensityOverride !== undefined
            ? { massDensityOverride: d.massDensityOverride }
            : {}),
    };
    if (d.isPointRoi) {
        return d.pointMm ? { ...out, markerPositionCm: cm(d.pointMm) } : out;
    }
    const v = deps.volumeCm3(d.roiNumber);
    const s = deps.stats(d.roiNumber);
    return {
        ...out,
        ...(Number.isFinite(v)
            ? {
                  volumeCm3: round(v, 2),
                  // The panel's formula (useRoi): d = 2·∛(3V / 4π).
                  equivalentSphereDiameterCm: round(
                      2 * Math.cbrt((3 * v) / (4 * Math.PI)),
                      2
                  ),
              }
            : {}),
        ...(s && Number.isFinite(s.surfaceAreaMm2)
            ? { surfaceAreaCm2: round(s.surfaceAreaMm2 / 100, 2) }
            : {}),
        ...(s?.sphericity != null
            ? { sphericity: round(s.sphericity, 3) }
            : {}),
        ...(s?.centerOfMassMm ? { centerOfMassCm: cm(s.centerOfMassMm) } : {}),
        ...(s?.boundingBox
            ? {
                  extentCm: cm(
                      s.boundingBox.max.map(
                          (mx, i) => mx - s.boundingBox!.min[i]
                      )
                  ),
              }
            : {}),
    };
}
